import Link from "@whoppah/next/link";
import { useRouter } from "next/router";

import { Currency, SellerAccountStatus } from "@/Apollo/schema";
import { CHAT_ROUTE } from "@/common/static/routes.static";
import {
  formatAccountDate,
  formatAccountMoney,
} from "@/common/utils/account-format.utils";
import {
  HeldOrderLine,
  OTHER_HOLD_TAG,
  releaseCopyKeys,
  splitHeldOrders,
  sumPayouts,
} from "@/components/Account/payment/payment.utils";
import { usePaymentCopy } from "@/components/Account/payment/usePaymentCopy";
import { Heading } from "@/ui/cva/Heading";
import { InfoLabel } from "@/ui/cva/InfoLabel";
import { Body } from "@/ui/typography/Body";

export type StripeBalance = {
  available: number;
  pending: number;
  currency: Currency;
};

type MoneyBreakdownProps = {
  status: SellerAccountStatus;
  orders: HeldOrderLine[];
  balance: StripeBalance | null;
  currency: Currency;
};

/**
 * Two groups with their own subtotal: what waits for the account (orders
 * Whoppah holds, then the Stripe balance) and what waits for something else
 * (a dispute, return, damage claim or refund, tagged per order). A group with
 * nothing in it is not rendered; no line ever reads €0.00.
 */
export const MoneyBreakdown = ({
  status,
  orders,
  balance,
  currency,
}: MoneyBreakdownProps) => {
  const { copy } = usePaymentCopy();
  const { locale } = useRouter();
  const money = (amount: number) =>
    formatAccountMoney(amount, { locale, currency, place: "accounting" });
  const { account, other } = splitHeldOrders(orders);
  const balanceTotal = balance
    ? Math.round((balance.available + balance.pending) * 100) / 100
    : 0;
  const accountTotal =
    Math.round((sumPayouts(account) + balanceTotal) * 100) / 100;
  const otherTotal = sumPayouts(other);
  const release = releaseCopyKeys(status);

  if (accountTotal <= 0 && otherTotal <= 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="payment-waiting-title"
      className="flex flex-col gap-4"
    >
      <Heading as="h2" size="h5" id="payment-waiting-title">
        {copy("sectionWaiting")}
      </Heading>
      {accountTotal > 0 && (
        <div className="flex flex-col gap-3" data-group="account">
          <div className="flex items-baseline justify-between gap-4 border-b border-grey-200 pb-2">
            <Body as="p" className="font-bold">
              {copy(release.group)}
            </Body>
            <Body as="p" className="font-bold tabular-nums">
              {money(accountTotal)}
            </Body>
          </div>
          <ul className="flex flex-col gap-3">
            {account.map(order => (
              <li key={order.id} className="flex flex-col gap-0.5">
                <div className="flex items-baseline justify-between gap-4">
                  <Body as="span">{order.title}</Body>
                  <Body as="span" className="tabular-nums">
                    {money(order.payout)}
                  </Body>
                </div>
                {order.created && (
                  <Body as="p" color="muted" className="text-sm">
                    {copy(release.order, {
                      date: formatAccountDate(order.created, locale),
                    })}
                  </Body>
                )}
              </li>
            ))}
            {balanceTotal > 0 && (
              <li className="flex flex-col gap-0.5" data-line="stripe-balance">
                <div className="flex items-baseline justify-between gap-4">
                  <Body as="span">{copy("balanceLine")}</Body>
                  <Body as="span" className="tabular-nums">
                    {money(balanceTotal)}
                  </Body>
                </div>
                <Body as="p" color="muted" className="text-sm">
                  {copy(release.balance)}
                </Body>
              </li>
            )}
          </ul>
        </div>
      )}
      {otherTotal > 0 && (
        <div className="flex flex-col gap-3" data-group="other">
          <div className="flex items-baseline justify-between gap-4 border-b border-grey-200 pb-2">
            <Body as="p" className="font-bold">
              {copy("groupOther")}
            </Body>
            <Body as="p" className="font-bold tabular-nums">
              {money(otherTotal)}
            </Body>
          </div>
          <ul className="flex flex-col gap-3">
            {other.map(order => {
              const tagKey = OTHER_HOLD_TAG[order.holdReason];

              return (
                <li key={order.id} className="flex flex-col gap-1">
                  <div className="flex items-baseline justify-between gap-4">
                    <Body as="span">{order.title}</Body>
                    <Body as="span" className="tabular-nums">
                      {money(order.payout)}
                    </Body>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {tagKey && (
                      <InfoLabel color="curious" size="small" corners="rounded">
                        {copy(tagKey)}
                      </InfoLabel>
                    )}
                    <Body as="span" color="muted" className="text-sm">
                      {copy("otherRelease")}
                    </Body>
                    {order.threadId && (
                      <Link
                        href={`${CHAT_ROUTE}/${order.threadId}`}
                        className="text-sm underline"
                      >
                        {copy("viewConversation")}
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};
